import React, { useReducer, useCallback } from 'react'
import _ from 'lodash'
import { getCategoryListPromise, getResourceAuthPromise } from 'apis/Workout'
import { getLanguageSuffixPromise } from 'apis/utils'
import {
  getResource,
  setResource,
  getResourceVersion,
  setResourceVersion,
  setCategoryList,
  setLanguageSuffix,
} from 'utils/resourceLocalStorage'
export const ResourceContext = React.createContext({})

const initialState = {
  workouts: getResource(),
}

const __version = '1.3.8'

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_RESOURCE':
      return {
        ...state,
        workouts: action.payload,
      }
    default:
      throw new Error()
  }
}

const getVersionMap = (r) => {
  const output = {}
  if (!r) return output
  r.forEach((w) => (output[w.resourceId] = w.version))
  return output
}

export const ResourceContextProvider = (Children) => {
  const Provider = (props) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const initResource = useCallback(async () => {
      let options
      let r = getResource() || []
      if (getResourceVersion() !== __version) {
        options = {}
        r = []
      } else {
        options = getVersionMap(r)
      }
      let [resources, categoryList, languageSuffix] = await Promise.all([
        getResourceAuthPromise(options),
        getCategoryListPromise(),
        getLanguageSuffixPromise(),
      ])
      setResourceVersion(__version)
      if (resources.length) {
        resources = _.uniqBy([...resources, ...r], 'resourceId')
        setResource(resources)
        dispatch({
          type: 'SET_RESOURCE',
          payload: resources,
        })
      }

      setCategoryList(categoryList)
      setLanguageSuffix(languageSuffix && languageSuffix.data)
    }, [])

    const forceUpdateResource = useCallback(async () => {
      let [resources, categoryList, languageSuffix] = await Promise.all([
        getResourceAuthPromise(),
        getCategoryListPromise(),
        getLanguageSuffixPromise(),
      ])
      setResourceVersion(__version)
      if (resources.length) {
        resources = _.uniqBy([...resources], 'resourceId')
        setResource(resources)
        dispatch({
          type: 'SET_RESOURCE',
          payload: resources,
        })
      }

      setCategoryList(categoryList)
      setLanguageSuffix(languageSuffix && languageSuffix.data)
    }, [])

    const getWorkoutFromResource = useCallback(
      (workout) => {
        const target =
          _.find(state.workouts, { resourceId: workout.resourceId }) || {}
        return { ...workout, ...target, ...(workout.part || {}) }
      },
      [state.workouts]
    )

    const getWorkoutFromResourceById = useCallback(
      (workoutId) => {
        const target = _.find(state.workouts, { resourceId: +workoutId }) || {}
        return { ...target }
      },
      [state.workouts]
    )

    const isWorkoutExistInResource = useCallback(
      (workoutId) => {
        const target = _.find(state.workouts, { resourceId: +workoutId }) || {}
        return !_.isEmpty(target)
      },
      [state.workouts]
    )

    return (
      <ResourceContext.Provider
        value={{
          state,
          dispatch,
          initResource,
          forceUpdateResource,
          getWorkoutFromResource,
          getWorkoutFromResourceById,
          isWorkoutExistInResource,
        }}
      >
        <Children {...props} />
      </ResourceContext.Provider>
    )
  }
  return Provider
}
